import EmptyState from '@components/Common/EmptyState';
import Panel from '@components/Common/Panel';
import { useCurrentUserRole } from '@components/Common/Utils/roles';
import {
  companyRequestsList,
  employers,
  jobApplicantsList,
  listResumeUploadsQuery,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import StatTile from '@components/Common/StatTile';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  type AttentionKey,
  importDayBounds,
  needsAttention,
  queuesFor,
} from './attentionQueues';

/**
 * One count per queue, each with size 1: only `totalElements` is read, so the page is never
 * transferred. Every filter here is the one the queue's link applies (see attentionQueues.ts).
 */
const COUNTERS: Record<AttentionKey, () => Promise<unknown>> = {
  companyRequests: async () =>
    (await fetchGraphQLApiData(companyRequestsList(0, 1, ['{ key: "status", value: "pending" }'])))
      ?.data?.getAllCompanyRequests?.totalElements ?? null,
  accountReviews: async () =>
    (await fetchGraphQLApiData(employers(0, 1, ['{ key: "approvalStatus", value: "PENDING", operator: "AND" }'])))
      ?.data?.getAllEmployers?.totalElements ?? null,
  premiumRequests: async () =>
    (await fetchGraphQLApiData(employers(0, 1, [
      '{ key: "premiumRequestStatus", value: "PENDING", operator: "AND" }',
      '{ key: "isPremium", value: "false", operator: "AND" }',
    ])))?.data?.getAllEmployers?.totalElements ?? null,
  agreementsSent: async () =>
    (await fetchGraphQLApiData(employers(0, 1, [
      '{ key: "premiumRequestStatus", value: "CONTRACT_SENT", operator: "AND" }',
      '{ key: "isPremium", value: "false", operator: "AND" }',
    ])))?.data?.getAllEmployers?.totalElements ?? null,
  awaitingFeedback: async () =>
    (await fetchGraphQLApiData(jobApplicantsList(0, 1, [
      '{ key: "status", value: "Submitted" }',
      '{ key: "subStatus", value: "Feedback pending" }',
    ])))?.data?.getAllJobApplications?.totalElements ?? null,
  failedImports: async () => {
    const { from, to } = importDayBounds();
    return (await fetchGraphQLApiData(listResumeUploadsQuery('FAILED', from, to, 0, 1)))
      ?.data?.listResumeUploads?.totalElements ?? null;
  },
};

/** The import page coerces the same way: totalElements has arrived as a string before. */
const toCount = (v: unknown): number | null => {
  const n = typeof v === 'string' ? parseInt(v, 10) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * The first thing on the Overview: what is waiting on this person, with a link to each list.
 *
 * Empty queues are left out, so the strip is short on a good day and says so when it is clear.
 * It never claims "nothing needs attention" over a count that failed to load.
 */
const NeedsAttention: React.FC = () => {
  const role = useCurrentUserRole();
  const [counts, setCounts] = useState<Partial<Record<AttentionKey, number | null>> | null>(null);

  useEffect(() => {
    const queues = queuesFor(role);
    if (queues.length === 0) return undefined;
    let cancelled = false;
    setCounts(null);
    Promise.all(
      queues.map((q) =>
        COUNTERS[q.key]()
          .then((v) => [q.key, toCount(v)] as const)
          // One failed queue must not cost the others their numbers.
          .catch(() => [q.key, null] as const),
      ),
    ).then((pairs) => {
      if (!cancelled) setCounts(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [role]);

  if (queuesFor(role).length === 0) return null;

  const summary = counts ? needsAttention(role, counts) : null;

  return (
    <Panel className="db-section">
      <Box className="db-section-header">
        <Typography className="db-section-title" variant="subtitle1">
          Needs attention
        </Typography>
        <Typography className="db-section-sub" variant="body2">
          Queues waiting on a decision or a next step. Each opens the list it counts.
        </Typography>
      </Box>

      {!summary ? (
        <Box className="cc-stat-grid">
          {[0, 1].map((i) => (
            <StatTile key={i} label="" value="" loading />
          ))}
        </Box>
      ) : summary.items.length === 0 ? (
        <EmptyState
          compact
          title={summary.incomplete ? 'Some queues could not be counted' : 'Nothing needs attention'}
          body={summary.incomplete ? 'Refresh to try again.' : undefined}
        />
      ) : (
        <>
          <Box className="cc-stat-grid">
            {summary.items.map((item) => (
              <StatTile
                key={item.key}
                label={item.label}
                value={item.count.toLocaleString('en-IN')}
                to={item.to}
                linkLabel={`${item.linkLabel} (${item.count})`}
              />
            ))}
          </Box>
          {summary.incomplete && (
            <Typography className="db-section-sub" variant="body2">
              Some queues could not be counted. Refresh to try again.
            </Typography>
          )}
        </>
      )}
    </Panel>
  );
};

export default NeedsAttention;
